import { KindeProvider as Provider, useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { type ReactNode, useEffect } from 'react'

import { setAccessTokenGetter } from '@/api/client'

import { useConfig } from '../hooks/useConfig'
import useRegisterLoadingState from '@/hooks/useRegisterLoadingState'

/**
 * Inner component that registers the access token getter with the axios interceptor.
 * This must be inside the Kinde Provider to access the useKindeAuth hook.
 * The interceptor will automatically add the Authorization header to all API requests.
 */
function TokenInterceptorSetup() {
  const { getAccessToken } = useKindeAuth()

  useEffect(() => {
    // Register the access token getter function with the axios interceptor.
    // This allows the interceptor to automatically retrieve and add the token to requests.
    // Note: We pass getAccessToken directly, not calling it here.
    // The interceptor will call it when needed, avoiding unnecessary token fetches.
    setAccessTokenGetter(async () => {
      try {
        const token = await getAccessToken()
        return token ?? null
      } catch (error) {
        console.warn('Failed to get access token:', error)
        return null
      }
    })

    // Cleanup: unregister the access token getter when component unmounts
    return () => {
      setAccessTokenGetter(() => Promise.resolve(null))
    }
    // Empty dependency array - only run once on mount
    // getAccessToken is a stable function from Kinde SDK, doesn't need to trigger re-runs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}

export function KindeProvider({ children }: { children: ReactNode }) {
  const { config, loading, error } = useConfig()

  // Register loading state with global loading system
  useRegisterLoadingState('kinde', loading)

  if (error) return <div className="text-red-600 dark:text-red-400">Error loading config: {error}</div>
  if (!config) return null

  const { clientId, domain } = config.kinde

  if (!clientId || !domain) {
    throw new Error('Missing required kinde config to initialize KindeProvider')
  }

  return (
    <Provider
      clientId={clientId}
      domain={domain}
      useInsecureForRefreshToken={process.env.NODE_ENV === 'development'}
      redirectUri={`${window.location.origin}/auth/callback`}
      logoutUri={window.location.origin}
      callbacks={{
        onSuccess: () => {
          window.location.pathname = '/'
        },
      }}
    >
      <TokenInterceptorSetup />
      {children}
    </Provider>
  )
}
